import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"
import { requireInventoryUserId } from "../lib/auth.server"
import {
  addSerialsToDraft,
  commitBatch,
  createDraftBatch,
  discardDraftBatch,
  getBatch,
  listBatches,
  removeSerialFromDraft,
  updateDraftBatch,
} from "../lib/receive.server"

const draftMetaSchema = z.object({
  vendorId: z.number(),
  productId: z.number(),
  invoiceNumber: z.string(),
  unitCostCents: z.number().int().nonnegative().nullable().optional(),
  notes: z.string().nullable().optional(),
  carrierId: z.number().nullable().optional(),
})

export const listBatchesFn = createServerFn({ method: "GET" }).handler(
  async () => {
    await requireInventoryUserId()
    return listBatches()
  },
)

export const getBatchFn = createServerFn({ method: "GET" })
  .validator((data: unknown) => z.object({ batchId: z.number() }).parse(data))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    return getBatch(data.batchId)
  })

export const createDraftBatchFn = createServerFn({ method: "POST" })
  .validator((data: unknown) => draftMetaSchema.parse(data))
  .handler(async ({ data }) => {
    const userId = await requireInventoryUserId()
    return createDraftBatch(userId, data)
  })

export const updateDraftBatchFn = createServerFn({ method: "POST" })
  .validator((data: unknown) =>
    z
      .object({
        batchId: z.number(),
        vendorId: z.number().optional(),
        productId: z.number().optional(),
        invoiceNumber: z.string().optional(),
        unitCostCents: z.number().int().nonnegative().nullable().optional(),
        notes: z.string().nullable().optional(),
        carrierId: z.number().nullable().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    const { batchId, ...meta } = data
    return updateDraftBatch(batchId, meta)
  })

export const addSerialsToDraftFn = createServerFn({ method: "POST" })
  .validator((data: unknown) =>
    z
      .object({
        batchId: z.number(),
        serials: z.array(z.string()),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    return addSerialsToDraft(data.batchId, data.serials)
  })

export const removeSerialFromDraftFn = createServerFn({ method: "POST" })
  .validator((data: unknown) =>
    z
      .object({
        batchId: z.number(),
        serialNumber: z.string(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    return removeSerialFromDraft(data.batchId, data.serialNumber)
  })

export const commitBatchFn = createServerFn({ method: "POST" })
  .validator((data: unknown) => z.object({ batchId: z.number() }).parse(data))
  .handler(async ({ data }) => {
    const userId = await requireInventoryUserId()
    return commitBatch(data.batchId, userId)
  })

export const discardDraftBatchFn = createServerFn({ method: "POST" })
  .validator((data: unknown) => z.object({ batchId: z.number() }).parse(data))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    return discardDraftBatch(data.batchId)
  })
